import { eq, useLiveQuery } from '@tanstack/react-db';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { SelectField, SwitchField, TextareaField, TextField } from '@/components/form-fields';
import { useDetailTitle } from '@/components/page-header';
import { EmptyState, ReadOnlyNotice, SectionCard } from '@/components/settings/settings-kit';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import {
  brandsCollection,
  type CollectionRecord,
  type CollectionRuleRecord,
  categoriesCollection,
  collectionRulesCollection,
  collectionsCollection,
  productTagsCollection,
} from '@/lib/collections';

/**
 * One collection, and the rules that decide what is in it.
 *
 * Membership is evaluated, not stored: the storefront runs these rules
 * against the live catalog on every request, which is why a collection can
 * be edited without touching a single product. The rules builder is the
 * piece the catalog phase left out -- until now the rows existed and only
 * SQL could write them.
 */
export const Route = createFileRoute('/collections/$collectionId')({
  /**
   * The stores this screen reads are synced on demand; without a
   * preload the first render sees empty tables and shows raw ids
   * where it means to show names.
   */
  loader: async () => {
    await Promise.all([
      collectionsCollection.preload(),
      collectionRulesCollection.preload(),
      categoriesCollection.preload(),
      brandsCollection.preload(),
      productTagsCollection.preload(),
    ]).catch(() => undefined);
  },
  component: CollectionDetailPage,
});

/** What the storefront's matcher understands. Anything else would never match. */
const FIELDS = [
  { value: 'category', label: 'Category' },
  { value: 'brand', label: 'Brand' },
  { value: 'tag', label: 'Tag' },
  { value: 'strain_type', label: 'Strain type' },
  { value: 'thc_percent', label: 'THC percent' },
  { value: 'price_cents', label: 'Price' },
];

const OPERATORS = [
  { value: 'eq', label: 'is' },
  { value: 'in', label: 'is any of' },
  { value: 'gte', label: 'is at least' },
  { value: 'lte', label: 'is at most' },
];

const STRAIN_TYPES = [
  'sativa',
  'indica',
  'hybrid',
  'sativa_hybrid',
  'indica_hybrid',
  'cbd',
  'ratio_1_1',
];

function CollectionDetailPage() {
  const { collectionId } = useParams({ from: '/collections/$collectionId' });
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';

  const canManage = can('catalog.manage');

  const found = useLiveQuery((query) =>
    query
      .from({ collection: collectionsCollection })
      .where(({ collection }) => eq(collection.id, collectionId)),
  );

  const collection = (found.data?.[0] ?? null) as CollectionRecord | null;

  const rules = useLiveQuery((query) =>
    query
      .from({ rule: collectionRulesCollection })
      .where(({ rule }) => eq(rule.collection_id, collectionId)),
  );

  useDetailTitle(collection?.name ?? 'Collection');

  if (found.isLoading) return <p className="text-muted-foreground">Loading…</p>;

  if (!collection) {
    return (
      <EmptyState>
        No such collection.{' '}
        <Link to="/collections" className="underline">
          Back to collections
        </Link>
      </EmptyState>
    );
  }

  const update = (patch: Partial<CollectionRecord>) => {
    collectionsCollection.update(collectionId, (draft) => Object.assign(draft, patch));
  };

  return (
    <div className="max-w-3xl space-y-6">
      {!canManage && <ReadOnlyNotice permission="catalog.manage" />}

      <fieldset disabled={!canManage} className="space-y-6">
        <SectionCard title="Collection" description="How it appears in the storefront's menu.">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="collection-name"
              label="Name"
              value={collection.name}
              onChange={(value) => update({ name: value })}
            />
            <TextField
              id="collection-slug"
              label="Address"
              description="Shown at /collections/<address>."
              value={collection.slug}
              onChange={(value) => update({ slug: value })}
            />
          </div>

          <TextareaField
            id="collection-description"
            label="Description"
            rows={3}
            value={collection.description ?? ''}
            onChange={(value) => update({ description: value })}
          />

          <SwitchField
            id="collection-active"
            label="Shown in the storefront"
            checked={collection.active ?? false}
            onChange={(checked) => update({ active: checked })}
          />
        </SectionCard>

        <SectionCard
          title="Rules"
          description="Membership is worked out from these every time the collection is shown."
        >
          <SelectField
            id="collection-match"
            label="A product belongs when"
            className="w-56"
            value={collection.rules_match ?? 'all'}
            onChange={(value) => update({ rules_match: value })}
            options={[
              { value: 'all', label: 'every rule matches' },
              { value: 'any', label: 'any rule matches' },
            ]}
          />

          {rules.data?.length ? (
            <ul className="divide-y rounded-lg border">
              {(rules.data as CollectionRuleRecord[]).map((rule) => (
                <RuleRow key={rule.id} rule={rule} canManage={canManage} />
              ))}
            </ul>
          ) : (
            <EmptyState>
              No rules, so nothing is in it. A collection with no rules shows an empty page.
            </EmptyState>
          )}

          {canManage && <AddRule accountId={accountId} collectionId={collectionId} />}
        </SectionCard>
      </fieldset>
    </div>
  );
}

function RuleRow({ rule, canManage }: { rule: CollectionRuleRecord; canManage: boolean }) {
  const field = FIELDS.find((option) => option.value === rule.field)?.label ?? rule.field;
  const operator =
    OPERATORS.find((option) => option.value === rule.operator)?.label ?? rule.operator;

  const categories = useLiveQuery((query) => query.from({ term: categoriesCollection }));
  const brands = useLiveQuery((query) => query.from({ term: brandsCollection }));
  const tags = useLiveQuery((query) => query.from({ term: productTagsCollection }));

  /*
   * The stored value is an id for the three fields that name a record --
   * that is what the storefront's matcher compares against -- so it is
   * resolved to a name here rather than shown as the UUID it is.
   */
  const named = (raw: unknown): string => {
    const id = String(raw ?? '');
    const lists: Record<string, { id: string; name: string }[]> = {
      category: categories.data ?? [],
      brand: brands.data ?? [],
      tag: tags.data ?? [],
    };

    return lists[rule.field]?.find((term) => term.id === id)?.name ?? id;
  };

  const value = Array.isArray(rule.value) ? rule.value.map(named).join(', ') : named(rule.value);

  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <span>
        <span className="text-muted-foreground">{field}</span> {operator}{' '}
        <span className="font-medium">{value}</span>
      </span>

      {canManage && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => collectionRulesCollection.delete(rule.id)}
        >
          <Trash2 className="size-4" />
          <span className="sr-only">Remove rule</span>
        </Button>
      )}
    </li>
  );
}

/**
 * The builder.
 *
 * The value control follows the field, because a rule that names a category
 * by hand is a typo waiting to match nothing: categories, brands and tags
 * are picked from what exists, and only the numeric fields are typed.
 */
function AddRule({ accountId, collectionId }: { accountId: string; collectionId: string }) {
  const [field, setField] = useState('category');
  const [operator, setOperator] = useState('eq');
  const [value, setValue] = useState('');

  const categories = useLiveQuery((query) => query.from({ term: categoriesCollection }));
  const brands = useLiveQuery((query) => query.from({ term: brandsCollection }));
  const tags = useLiveQuery((query) => query.from({ term: productTagsCollection }));

  const options = (): { value: string; label: string }[] => {
    /*
     * Ids, not slugs. The storefront matches a rule against
     * `products.category_id` / `brand_id` / its tag ids, so a rule holding a
     * slug would be valid, savable and match nothing.
     */
    if (field === 'category') {
      return (categories.data ?? []).map((term) => ({ value: term.id, label: term.name }));
    }

    if (field === 'brand') {
      return (brands.data ?? []).map((term) => ({ value: term.id, label: term.name }));
    }

    if (field === 'tag') {
      return (tags.data ?? []).map((term) => ({ value: term.id, label: term.name }));
    }

    if (field === 'strain_type') {
      return STRAIN_TYPES.map((type) => ({ value: type, label: type.replace(/_/g, ' ') }));
    }

    return [];
  };

  const choices = options();
  const numeric = field === 'thc_percent' || field === 'price_cents';

  const add = () => {
    const trimmed = value.trim();

    if (!trimmed) {
      toast.error('Give the rule something to match.');
      return;
    }

    collectionRulesCollection.insert({
      // The table's id is a bigint identity; the store needs a key before the
      // insert round-trips, and the server's value replaces it.
      id: Date.now(),
      account_id: accountId,
      collection_id: collectionId,
      field,
      operator,
      value: operator === 'in' ? trimmed.split(',').map((part) => part.trim()) : trimmed,
    });

    setValue('');
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <SelectField
        id="rule-field"
        label="Field"
        className="w-40"
        value={field}
        onChange={(next) => {
          setField(next);
          setValue('');
        }}
        options={FIELDS}
      />

      <SelectField
        id="rule-operator"
        label="Test"
        className="w-36"
        value={operator}
        onChange={setOperator}
        options={
          numeric
            ? OPERATORS
            : OPERATORS.filter((option) => option.value !== 'gte' && option.value !== 'lte')
        }
      />

      {choices.length && operator === 'eq' ? (
        <SelectField
          id="rule-value"
          label="Value"
          className="w-56"
          value={value}
          onChange={setValue}
          options={choices}
        />
      ) : (
        <TextField
          id="rule-value"
          label="Value"
          className="w-56"
          description={operator === 'in' ? 'Comma separated.' : undefined}
          value={value}
          onChange={setValue}
        />
      )}

      <Button type="button" onClick={add}>
        <Plus className="size-4" />
        Add rule
      </Button>
    </div>
  );
}
